import { useState, useRef, useMemo, useEffect, type ReactNode } from 'react';
import { Camera, FileText, Upload, X } from 'lucide-react';
import {
  // Compact variant styles
  uploadZone,
  uploadZoneActive,
  uploadIcon,
  uploadTextContainer,
  uploadLabel,
  uploadHint,
  // Large variant styles
  uploadZoneLarge,
  uploadZoneLargeActive,
  uploadIconLarge,
  uploadLabelLarge,
  uploadHintLarge,
  // Shared styles
  previewGrid,
  previewItem,
  previewImage,
  removeButton,
  removeButtonDisabled,
  // File list styles (non-image files)
  fileListContainer,
  fileListItem,
  fileListIcon,
  fileListName,
  fileListSize,
  fileListRemoveButton,
  fileListRemoveButtonDisabled,
} from '@/styles/fileUpload';
import { formatFileSize } from '@/utils/format';

interface FileUploadProps {
  /** Accepted file types - supports MIME types (e.g., "image/jpeg") or extensions (e.g., ".csv") */
  accept: string;
  /** Maximum number of files allowed (default: 1) */
  maxFiles?: number;
  /** Maximum file size in bytes (default: 10MB) */
  maxFileSize?: number;
  /** Allow multiple file selection (default: false) */
  multiple?: boolean;

  /** Currently selected files (controlled component) */
  files: File[];
  /** Callback when files change */
  onFilesChange: (files: File[]) => void;

  /** Error callback for validation failures */
  onError?: (message: string) => void;

  /** UI variant: 'compact' for inline form fields, 'large' for centered dropzones (default: 'compact') */
  variant?: 'compact' | 'large';
  /** Primary label text (default: "Add Files" for compact, customizable for large) */
  label?: string;
  /** Secondary hint text (e.g., "Up to 3 · JPEG, PNG · 10MB max") */
  hint?: string;
  /** Custom icon (default: Camera for compact, Upload for large) */
  icon?: ReactNode;
  /** Icon size in pixels (default: 20 for compact, 48 for large) */
  iconSize?: number;
  /** Disable the upload zone */
  disabled?: boolean;
  /** Show image previews (default: true for images) */
  showPreviews?: boolean;
  /** Number of columns in preview grid (default: 3) */
  previewColumns?: number;

  /** Camera capture mode for mobile ("user" for front, "environment" for back) */
  capture?: 'user' | 'environment';

  /** HTML id attribute for the hidden file input (useful for E2E testing) */
  id?: string;
}

/**
 * Reusable file upload component with drag-and-drop support.
 *
 * Features:
 * - Click to upload with hidden file input
 * - Drag and drop with visual feedback
 * - File validation (type, size, count)
 * - Image previews with remove buttons
 * - Keyboard accessible
 * - Mobile camera capture support
 *
 * @example
 * ```tsx
 * <FileUpload
 *   accept="image/jpeg,image/png,image/webp"
 *   maxFiles={3}
 *   maxFileSize={10 * 1024 * 1024}
 *   multiple
 *   files={selectedFiles}
 *   onFilesChange={setSelectedFiles}
 *   onError={(msg) => toast.error(msg)}
 *   label="Add Photos"
 *   hint="Up to 3 · JPEG, PNG, WebP · 10MB max"
 *   capture="environment"
 * />
 * ```
 */
export function FileUpload({
  accept,
  maxFiles = 1,
  maxFileSize = 10 * 1024 * 1024,
  multiple = false,
  files,
  onFilesChange,
  onError,
  variant = 'compact',
  label = 'Add Files',
  hint,
  icon,
  iconSize,
  disabled = false,
  showPreviews = true,
  previewColumns = 3,
  capture,
  id,
}: FileUploadProps) {
  // Determine default icon based on variant
  const defaultIconSize = variant === 'large' ? 48 : 20;
  const effectiveIconSize = iconSize ?? defaultIconSize;
  const defaultIcon =
    variant === 'large' ? (
      <Upload size={effectiveIconSize} aria-hidden="true" />
    ) : (
      <Camera size={effectiveIconSize} aria-hidden="true" />
    );
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Track preview URLs in a Map to handle controlled component updates
  // and properly revoke URLs when files are removed
  const urlMapRef = useRef<Map<File, string>>(new Map());

  const previewUrls = useMemo(() => {
    const newMap = new Map<File, string>();

    // Revoke URLs for files that were removed
    for (const [file, url] of urlMapRef.current) {
      if (!files.includes(file)) {
        URL.revokeObjectURL(url);
      } else {
        newMap.set(file, url);
      }
    }

    // Create URLs for new files
    for (const file of files) {
      if (!newMap.has(file)) {
        newMap.set(file, URL.createObjectURL(file));
      }
    }

    urlMapRef.current = newMap;
    return files.map((f) => newMap.get(f) ?? '');
  }, [files]);

  // Cleanup all URLs on unmount
  useEffect(() => {
    return () => {
      for (const url of urlMapRef.current.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // Validate and process files
  const processFiles = (fileList: FileList | null) => {
    if (!fileList || disabled) return;

    const newFiles = Array.from(fileList);
    const allowedTypes = accept.split(',').map((t) => t.trim());

    // Check total count
    if (files.length + newFiles.length > maxFiles) {
      onError?.(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    // Validate each file
    for (const file of newFiles) {
      // Check both MIME types and file extensions
      const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
      const isValidType = allowedTypes.some((allowed) => {
        // Check if it's a MIME type match (contains '/')
        if (allowed.includes('/')) {
          return file.type === allowed;
        }
        // Check if it's an extension match (starts with '.')
        return fileExtension === allowed.toLowerCase();
      });

      if (!isValidType) {
        onError?.(`Invalid file type: ${file.name}`);
        return;
      }
      if (file.size > maxFileSize) {
        const maxMB = Math.round(maxFileSize / 1024 / 1024);
        onError?.(`File too large: ${file.name}. Maximum size is ${maxMB}MB.`);
        return;
      }
    }

    onFilesChange([...files, ...newFiles]);
  };

  // Remove a file at the given index
  const removeFile = (index: number) => {
    if (disabled) return;
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  // File input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    e.target.value = ''; // Reset for re-selection of same file
  };

  // Drag and drop handlers using counter pattern to prevent flicker
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    dragCounterRef.current++;
    setIsDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    if (!disabled) {
      processFiles(e.dataTransfer.files);
    }
  };

  // Keyboard handler for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  // Determine zone style based on state and variant
  const getZoneStyle = () => {
    const baseStyle = variant === 'large' ? uploadZoneLarge : uploadZone;
    const activeStyle = variant === 'large' ? uploadZoneLargeActive : uploadZoneActive;

    if (disabled) return { ...baseStyle, cursor: 'not-allowed', opacity: 0.6 };
    if (isDragActive) return activeStyle;
    return baseStyle;
  };

  // Get text styles based on variant
  const getIconStyle = () => (variant === 'large' ? uploadIconLarge : uploadIcon);
  const getLabelStyle = () => (variant === 'large' ? uploadLabelLarge : uploadLabel);
  const getHintStyle = () => (variant === 'large' ? uploadHintLarge : uploadHint);

  // Check if we should show the upload zone (hide when max files reached)
  const showUploadZone = files.length < maxFiles;

  // Check if files are images for preview display
  const isImageFile = (file: File) => file.type.startsWith('image/');
  const shouldShowPreviews = showPreviews && files.length > 0 && files.some(isImageFile);

  return (
    <div>
      {/* Upload Zone */}
      {showUploadZone && (
        <label
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={getZoneStyle()}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-label={label}
          aria-disabled={disabled}
          onKeyDown={handleKeyDown}
        >
          <div style={getIconStyle()}>{icon || defaultIcon}</div>
          {variant === 'large' ? (
            // Large variant: vertical layout, no container wrapper
            <>
              <span style={getLabelStyle()}>{label}</span>
              {hint && <span style={getHintStyle()}>{hint}</span>}
            </>
          ) : (
            // Compact variant: horizontal layout with text container
            <div style={uploadTextContainer}>
              <span style={getLabelStyle()}>{label}</span>
              {hint && <span style={getHintStyle()}>{hint}</span>}
            </div>
          )}
          <input
            type="file"
            id={id}
            accept={accept}
            multiple={multiple}
            capture={capture}
            onChange={handleInputChange}
            disabled={disabled}
            ref={inputRef}
            style={{ display: 'none' }}
            aria-hidden="true"
          />
        </label>
      )}

      {/* Preview Grid — image files */}
      {shouldShowPreviews && (
        <div style={{ ...previewGrid, gridTemplateColumns: `repeat(${previewColumns}, 1fr)` }}>
          {files.map((file, index) => {
            // Only show previews for image files
            if (!isImageFile(file)) return null;

            return (
              <div key={`${file.name}-${index}`} style={previewItem}>
                <img
                  src={previewUrls[index]}
                  alt={`Preview ${index + 1}: ${file.name}`}
                  style={previewImage}
                />
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={disabled}
                  style={disabled ? removeButtonDisabled : removeButton}
                  title={`Remove ${file.name}`}
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* File List — non-image files (PDFs, docs, etc.) */}
      {/* Uses showPreviews prop (not shouldShowPreviews) because shouldShowPreviews
          requires at least one image file, which would hide the list for PDF-only uploads */}
      {showPreviews && files.some((f) => !isImageFile(f)) && (
        <div style={fileListContainer}>
          {files.map((file, index) => {
            if (isImageFile(file)) return null;
            return (
              <div key={`${file.name}-${index}`} style={fileListItem}>
                <FileText size={16} aria-hidden="true" style={fileListIcon} />
                <span style={fileListName}>{file.name}</span>
                <span style={fileListSize}>{formatFileSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={disabled}
                  style={disabled ? fileListRemoveButtonDisabled : fileListRemoveButton}
                  title={`Remove ${file.name}`}
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
